// ============================================================
// THE PLATE'S READOUT LINES (plan design-gaps, P4; the bench's readout box):
// what the plate says in words besides the location under the pointer -
//   the LAST HIT: the burst ("4 HITS · IMPACT"), what glanced off, where each
//     landed in runs ("LANDED BODY x2, HEAD"), and the line the target's own
//     nature adds (passes through the incorporeal, the holy that lands on it,
//     the field breaker, Witchbane on a psyker);
//   the SIGHTS: the place locked, or the choice still open;
//   a LOCKED place on a Strange Physiology body: no weak point to find.
// Upper case, as the plate prints them.
//
// Pure. Zero React dependency.
// ============================================================

import type { Impact } from '../impacts';
import { hasSpecial } from './specials';
import { DAMAGE_TYPE_COLORS } from '../constants';

/** What the readout reads off the target's nature (engine/plate/traitLooks). */
export interface ReadoutLooks {
  incorporeal: boolean;
  /** The force field's rating while it is up (0: none). */
  field: number;
  /** The psyker's rating (0: none). */
  psy: number;
}

/** The weapons that land on the incorporeal (the bench's HOLY), as their specials are named. */
const LANDS_ON_THE_INCORPOREAL = ['Force', 'Sanctified', 'WarpWeapon'];

type LastHit = Pick<Impact, 'outcome' | 'damage' | 'damageType' | 'hits' | 'dodged' | 'specials'>;

/**
 * Each place the hits landed, in runs: Body x2, Head (the bench's "Body x15, not fifteen Bodys"). A hit the field
 * blocked did not land (the Track P review), as the plate's own burst counts it.
 */
function landedRuns(hits: Impact['hits'], nameOf: (id: string) => string): string {
  const runs: { id: string; n: number }[] = [];
  for (const h of hits.filter((x) => !x.blocked)) {
    const id = h.location ?? 'body';
    const run = runs.at(-1);
    if (run?.id === id) run.n++;
    else runs.push({ id, n: 1 });
  }
  if (!runs.length) return 'LANDED NONE';
  return `LANDED ${runs.map((r) => `${nameOf(r.id).toUpperCase()}${r.n > 1 ? ` x${r.n}` : ''}`).join(', ')}`;
}

/** On the incorporeal: the holy weapon that lands, or the blow that passes through it; else nothing to say. */
function incorporealLine(last: LastHit): string | null {
  const holy = LANDS_ON_THE_INCORPOREAL.find((s) => hasSpecial(last.specials, s));
  if (holy) return `LANDS · ${holy.toUpperCase()}`;
  return last.damage > 0 ? null : 'PASSES THROUGH';
}

/** The lines the target's nature adds to the last hit, each when it holds. */
function natureLines(last: LastHit, looks: ReadoutLooks): string[] {
  return [
    looks.incorporeal ? incorporealLine(last) : null,
    looks.field > 0 && hasSpecial(last.specials, 'Fieldbreaker') ? 'FIELDBREAKER' : null,
    looks.psy > 0 && hasSpecial(last.specials, 'Witchbane') ? 'WITCHBANE · ITS POWER GUTTERS' : null,
  ].filter((line): line is string => line !== null);
}

/** How many hits the force field stopped, when any did. */
function blockedLine(hits: Impact['hits']): string[] {
  const n = hits.filter((h) => h.blocked).length;
  return n ? [`${n} BLOCKED BY THE FIELD`] : [];
}

/** The last hit, in words: the burst, what glanced, where it landed, and what the target's nature adds. */
export function lastHitLines(last: LastHit, looks: ReadoutLooks, nameOf: (id: string) => string): string[] {
  if (last.outcome === 'miss') return ['MISSED'];
  const n = last.hits.length + last.dodged;
  const type = last.damageType ? ` · ${DAMAGE_TYPE_COLORS[last.damageType].label.toUpperCase()}` : '';
  return [
    `${n} ${n === 1 ? 'HIT' : 'HITS'}${type}`,
    ...(last.dodged ? [`${last.dodged} GLANCED OFF`] : []),
    ...blockedLine(last.hits),
    landedRuns(last.hits, nameOf),
    ...natureLines(last, looks),
  ];
}

/**
 * The sights' line, once a place is locked. While the choice is open the plate says nothing - the brackets on the
 * figure already invite it (the user's ruling, plan plate-unboxed).
 */
export const sightsLine = (locked: string) => `SIGHTS · ${locked.toUpperCase()} LOCKED`;

/** What a locked place adds: on a Strange Physiology body, there is no weak point to find (the bench's). */
export const lockedLines = (locked: boolean, strange: boolean) =>
  locked && strange ? ['STRANGE PHYSIOLOGY · NO WEAK POINT'] : [];
